import { createFileRoute } from '@tanstack/react-router';
import {
  Layers,
  Server,
  Database,
  Radio,
  KeyRound,
  Activity,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Badge } from '@ouropix/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';

export const Route = createFileRoute('/_admin/presentation/architecture')({
  component: ArchitecturePage,
});

interface Microservice {
  domain: string;
  service: string;
  responsibility: string;
}

const MICROSERVICES: Microservice[] = [
  { domain: 'Auth', service: 'auth-svc', responsibility: 'register/login/OTP/reset' },
  { domain: 'User profile', service: 'user-svc', responsibility: 'profile, addresses, settings, consent' },
  { domain: 'Admin', service: 'admin-svc', responsibility: 'admin CRUD, invites, 2FA, step-up' },
  { domain: 'OTP', service: 'otp-svc', responsibility: 'gerar/validar/expirar OTP' },
  { domain: 'Ledger/Wallet', service: 'ledger-svc', responsibility: 'ledger imutável, balance' },
  { domain: 'Payment', service: 'payment-svc', responsibility: 'EFI/StarkBank, webhooks' },
  { domain: 'Game catalog', service: 'game-catalog-svc', responsibility: 'modalities/templates/themes/games CRUD' },
  { domain: 'Game engine', service: 'game-engine-svc', responsibility: 'purchase → reveal → credit' },
  { domain: 'Pool', service: 'pool-svc', responsibility: 'RNG offline, claim FOR UPDATE SKIP LOCKED' },
  { domain: 'Affiliate', service: 'affiliate-svc', responsibility: 'btags + conversion tracking' },
  { domain: 'Audit', service: 'audit-svc', responsibility: 'audit_signup/signin/financial/admin/changedata' },
  { domain: 'Fraud', service: 'fraud-svc', responsibility: 'velocity/device/IP anomaly' },
  { domain: 'Fulfillment', service: 'fulfillment-svc', responsibility: 'shipments físicos, Melhor Envio/Correios' },
  { domain: 'Media', service: 'media-svc', responsibility: 'uploads R2/S3, media_library' },
  { domain: 'Notification', service: 'notification-svc', responsibility: 'email (SES), SMS, templates' },
  { domain: 'Winners', service: 'winners-svc', responsibility: 'live_winner_feed, rankings' },
];

interface TechItem {
  label: string;
  value: string;
}

const TECH_STACK: TechItem[] = [
  { label: 'Language', value: 'TypeScript strict, Node 20 LTS' },
  { label: 'Monorepo', value: 'Turbo + npm workspaces' },
  { label: 'DB', value: 'Postgres 16 (Neon/self-hosted), Prisma 5' },
  { label: 'Cache/Queue', value: 'Redis 7, BullMQ' },
  { label: 'Realtime', value: 'Socket.IO 4' },
  { label: 'Auth', value: 'JWT RS256 + DB-backed CSRF + cookies __Host-' },
  { label: 'Secrets', value: '1Password Service Account COFRE_OUROPIX' },
  { label: 'Observability', value: 'OpenTelemetry + Sentry + Prometheus (EPICs RN-15..19)' },
];

const AUTH_FLOW = [
  'Frontend envia credencial → Gateway',
  'Gateway chama auth-svc.login() via gRPC → valida CPF+senha ou OTP',
  'auth-svc gera JWT RS256 (kid rotation), grava user_sessions',
  'Gateway seta cookie __Host-access_token + csrf_token DB-backed',
  'Requests subsequentes: JwtAuthGuard → RolesGuard → CsrfGuard (writes) → controller',
];

function ArchitecturePage() {
  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Arquitetura OuroPix</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Referência interna — mapa técnico da plataforma para admins e suporte.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4" />
            Camadas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="overflow-x-auto rounded-md bg-muted p-4 font-mono text-[11px] leading-relaxed">{`┌─────────────────────────────────────────────┐
│  Frontend                                   │
│  ├─ apps/site (Next.js)    — público/dashboard
│  └─ apps/admin (Vite + Tanstack Router)
└────────────────┬────────────────────────────┘
                 │ HTTPS (JWT RS256)
┌────────────────▼────────────────────────────┐
│  Gateway NestJS (apps/gateway)              │
│  Guards: JWT / Admin / CSRF / StepUp /
│  RateLimit / AnomalyDetection
└────────────────┬────────────────────────────┘
                 │ gRPC
┌────────────────▼────────────────────────────┐
│  16 Microservices (services/)               │
│  auth · user · admin · otp · wallet(ledger)
│  payment · game-catalog · game-engine · pool
│  affiliate · audit · fraud · fulfillment
│  media · notification · winners
└────────────────┬────────────────────────────┘
                 │
   ┌─────────────┼──────────────┬─────────────┐
   ▼             ▼              ▼             ▼
 Postgres     Redis          Socket.IO    1Password
(Prisma)    (rate+cache)   (realtime)   (secrets)`}</pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Server className="h-4 w-4" />
            Microservices (16)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Domínio</th>
                  <th className="pb-2 pr-4 font-medium">Service</th>
                  <th className="pb-2 font-medium">Responsabilidade</th>
                </tr>
              </thead>
              <tbody>
                {MICROSERVICES.map((m) => (
                  <tr key={m.service} className="border-b last:border-b-0">
                    <td className="py-2 pr-4 font-medium">{m.domain}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-primary">{m.service}</td>
                    <td className="py-2 text-muted-foreground">{m.responsibility}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4" />
              Tech stack
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              {TECH_STACK.map((t) => (
                <div key={t.label} className="grid grid-cols-[120px_1fr] gap-2">
                  <dt className="font-medium">{t.label}</dt>
                  <dd className="text-muted-foreground">{t.value}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4" />
              Fluxo de autenticação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-2 text-sm">
              {AUTH_FLOW.map((step, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                    {idx + 1}
                  </span>
                  <span className="text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4" />
            Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <Badge variant="default" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Core
            </Badge>
            <span className="text-sm text-muted-foreground">
              auth, admin, wallet, games, raffles — DONE
            </span>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="secondary" className="gap-1">
              <Clock className="h-3 w-3" />
              P0 pendentes
            </Badge>
            <span className="text-sm text-muted-foreground">
              EI-02 StarkBank, EI-03 Provider Routing, CSRF admin bearer gap
            </span>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="outline" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              P2 observability
            </Badge>
            <span className="text-sm text-muted-foreground">
              OTel / Prometheus / Sentry — PROPOSED
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Radio className="h-4 w-4" />
            Realtime channels (Socket.IO)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>
              <span className="font-mono text-xs">user:balance</span> — eventos de ledger (CREDIT/DEBIT)
            </li>
            <li>
              <span className="font-mono text-xs">user:notifications</span> — notificações em tempo real
            </li>
            <li>
              <span className="font-mono text-xs">winners:live</span> — broadcast de vencedores (live_winner_feed)
            </li>
            <li>
              <span className="font-mono text-xs">admin:presence</span> — presença de admins online
            </li>
            <li>
              <span className="font-mono text-xs">admin:alerts</span> — alertas críticos de segurança
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
